/**
 * Thousands-grouped money field.
 *
 * A seven-digit figure typed into a bare `<input type="number">` is exactly where someone drops
 * or adds a zero and never sees it, so the field shows the grouped form while it is being typed
 * rather than only after it is submitted.
 *
 * That rules out `type="number"`: it rejects the group separator outright, and a value the
 * browser considers invalid comes back from `e.target.value` as an empty string. The control is
 * therefore a text input that re-formats on every keystroke — which is why the parse side has to
 * exist too, and why nothing downstream may keep calling `Number()` on the raw field value.
 *
 * Separators are `en-US`, matching `formatCurrency`, so a field never disagrees with the figure
 * printed beside it. The client app has its own copy of this file using `vi-VN` separators for
 * the same reason; change one only by changing that app's `formatCurrency` with it.
 */

const GROUP_SEPARATOR = ",";
const DECIMAL_SEPARATOR = ".";

/** Digits a caller may type before the value stops being a plausible amount. */
const MAX_WHOLE_DIGITS = 15;

/** Groups a bare digit string in threes. */
function group(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
}

/**
 * Digits only, capped, with leading zeros dropped as they are typed rather than on blur —
 * "0" then "5" has to become "5", not "05", or the grouping lands on the wrong boundary.
 */
function wholePart(value: string): string {
  return value
    .replace(/\D/g, "")
    .slice(0, MAX_WHOLE_DIGITS)
    .replace(/^0+(?=\d)/, "");
}

/**
 * The grouped form of whatever was typed. Empty in, empty out — so the field can be cleared.
 *
 * `decimals` is the currency's own precision (`getDecimalPlaces`), not a display preference:
 * pass 0 for VND and the separator is simply not accepted, pass 2 for USD and a cent can be
 * typed. Hard-coding 0 here would quietly refuse to verify a $1.50 payment.
 */
export function formatAmountInput(value: string, decimals: number = 0): string {
  if (decimals <= 0) {
    const digits = wholePart(value);
    return digits === "" ? "" : group(digits);
  }

  // Split on the FIRST separator: everything after a second one is discarded, which is what a
  // keyboard produces when the key is pressed twice and is never what was meant.
  const [rawWhole = "", ...rest] = value.split(DECIMAL_SEPARATOR);
  const whole = wholePart(rawWhole);
  const hasSeparator = rest.length > 0;
  const fraction = rest.join("").replace(/\D/g, "").slice(0, decimals);

  if (whole === "" && !hasSeparator) {
    return "";
  }

  // ".5" becomes "0.5" as it is typed — a field reading "." alone parses to nothing, and the
  // operator would be looking at a disabled Confirm button with no idea why.
  const grouped = whole === "" ? "0" : group(whole);
  return hasSeparator ? `${grouped}${DECIMAL_SEPARATOR}${fraction}` : grouped;
}

/**
 * The number behind a grouped field, or null when the field holds no digits at all.
 * Null rather than 0 — an empty field and a deliberate zero are different answers, and only the
 * caller knows which of the two its validation should reject.
 */
export function parseAmountInput(value: string): number | null {
  const [rawWhole = "", ...rest] = value.split(DECIMAL_SEPARATOR);
  const whole = rawWhole.replace(/\D/g, "");
  const fraction = rest.join("").replace(/\D/g, "");

  if (whole === "" && fraction === "") {
    return null;
  }

  const parsed = Number(`${whole || "0"}.${fraction || "0"}`);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The grouped form of a number the app already holds — for seeding a field from a DTO.
 * `String(1000000)` is safe, but `String(1e21)` is not a digit string at all, so the conversion
 * goes through here rather than being spelled out at each call site.
 */
export function toAmountInput(amount: number, decimals: number = 0): string {
  if (!Number.isFinite(amount)) {
    return "";
  }

  return formatAmountInput(amount.toFixed(Math.max(0, decimals)), decimals);
}

/**
 * Where the caret belongs once the value has been re-formatted.
 *
 * Writing a controlled input's value throws the caret to the end of the field, so without this
 * every keystroke in the middle of "1,234,567" bounces the operator to the far right — a field
 * that is harder to correct than the plain one it replaced. Group separators move on their own
 * as digits are inserted, so the caret is anchored to what the operator actually typed: digits,
 * plus the decimal point, which is a character they placed themselves and can sit behind.
 *
 * Takes the browser's post-edit value and caret, not the previous ones: at that point the edit
 * has already been applied and only the grouping still has to catch up.
 */
export function caretAfterFormat(
  rawValue: string,
  rawCaret: number,
  formatted: string,
): number {
  const anchors = (char: string) =>
    (char >= "0" && char <= "9") || char === DECIMAL_SEPARATOR;

  let target = 0;
  for (let i = 0; i < rawCaret && i < rawValue.length; i++) {
    if (anchors(rawValue[i])) {
      target++;
    }
  }

  if (target === 0) {
    return 0;
  }

  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (anchors(formatted[i])) {
      seen++;
      if (seen === target) {
        return i + 1;
      }
    }
  }

  // Fewer anchors survived than the caret had passed — a leading zero was dropped, a surplus
  // decimal digit was trimmed, or the length cap bit. The end of the field is the only position
  // that still makes sense.
  return formatted.length;
}

/**
 * Whether a character is grouping the mask inserted, rather than something the operator typed.
 *
 * A separator cannot be deleted on its own — the mask puts it straight back — so Backspace and
 * Delete land on one and appear to do nothing at all. The field's key handler uses this to step
 * the caret over the separator first, so the browser's own deletion takes the digit beyond it
 * and one keypress removes one digit wherever the caret happens to be.
 *
 * The DECIMAL separator is deliberately not included: the operator placed it, and deleting it
 * is a real edit that turns "19.99" into "1999".
 */
export function isGroupSeparator(char: string | undefined): boolean {
  return char === GROUP_SEPARATOR;
}
